import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, X, User, ArrowLeft } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

export default function ImpersonationBanner() {
  const navigate = useNavigate();
  
  // Check impersonation status from localStorage
  const impersonating = typeof window !== 'undefined' && localStorage.getItem('is_impersonating') === 'true';
  const email = typeof window !== 'undefined' ? localStorage.getItem('impersonated_user_email') || '' : '';
  
  const [minimized, setMinimized] = useState(false);

  const handleReturnToAdmin = () => {
    // Restore admin token
    const adminToken = localStorage.getItem('admin_token_backup');
    const adminUser = localStorage.getItem('admin_user_backup');
    
    if (adminToken && adminUser) {
      localStorage.setItem('token', adminToken);
      localStorage.setItem('user', adminUser);
      
      // Clean up impersonation data
      localStorage.removeItem('admin_token_backup');
      localStorage.removeItem('admin_user_backup');
      localStorage.removeItem('is_impersonating');
      localStorage.removeItem('impersonated_user_email');
      
      toast.success('Returned to admin account');
      
      // Navigate to admin dashboard
      window.location.href = '/admin';
    } else {
      // Fallback: just clear impersonation and go to login
      localStorage.removeItem('is_impersonating');
      localStorage.removeItem('impersonated_user_email');
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      
      toast.info('Session expired. Please log in again.');
      navigate('/login');
    }
  };

  if (!impersonating) return null;

  if (minimized) {
    return (
      <button
        onClick={() => setMinimized(false)}
        className="fixed bottom-4 right-4 z-[9999] w-12 h-12 bg-gradient-to-r from-amber-500 to-orange-500 rounded-full shadow-lg flex items-center justify-center hover:scale-110 transition-transform animate-pulse"
        title="Viewing as user - Click to expand"
      >
        <Shield className="w-6 h-6 text-white" />
      </button>
    );
  }

  return (
    <div className="fixed bottom-4 right-4 z-[9999] animate-in slide-in-from-bottom-4">
      <div className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl shadow-2xl overflow-hidden max-w-sm">
        {/* Header */}
        <div className="px-4 py-2 bg-black/20 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-white" />
            <span className="text-white text-sm font-semibold">Admin View Mode</span>
          </div>
          <button
            onClick={() => setMinimized(true)}
            className="text-white/70 hover:text-white transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        
        {/* Content */}
        <div className="px-4 py-3">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
              <User className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-white/70 text-xs">Viewing as:</p>
              <p className="text-white font-medium text-sm truncate max-w-[180px]">
                {email}
              </p>
            </div>
          </div>
          
          <Button
            onClick={handleReturnToAdmin}
            className="w-full bg-white text-orange-600 hover:bg-white/90 font-semibold"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Return to Admin Dashboard
          </Button>
        </div>
      </div>
    </div>
  );
}
